import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';

interface MetricCardProps {
  title: string;
  description: string;
  value: number | string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon?: React.ReactNode;
}

export function MetricCard({ title, description, value, trend, icon }: MetricCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {icon && <div className="text-primary-600">{icon}</div>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline justify-between">
          <p className="text-3xl font-semibold text-neutral-900">{value}</p>
          {trend && (
            <span
              className={`text-sm font-medium ${
                trend.isPositive ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {trend.isPositive ? '+' : ''}
              {trend.value}%
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface DashboardMetricsProps {
  metrics: {
    activeRequests: number;
    pendingActions: number;
    completedToday: number;
    totalLineItems?: number;
    standardHolds?: number;
    exceptionHolds?: number;
  };
}

export function DashboardMetrics({ metrics }: DashboardMetricsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <MetricCard
        title="Active Requests"
        description="Requests in progress"
        value={metrics.activeRequests}
      />
      <MetricCard
        title="Pending Actions"
        description="Items requiring attention"
        value={metrics.pendingActions}
      />
      <MetricCard
        title="Completed Today"
        description="Requests completed today"
        value={metrics.completedToday}
      />
      {metrics.totalLineItems !== undefined && (
        <MetricCard
          title="Total Line Items"
          description="All inventory line items"
          value={metrics.totalLineItems}
        />
      )}
      {metrics.standardHolds !== undefined && (
        <MetricCard
          title="Standard Holds"
          description="Standard processing"
          value={metrics.standardHolds}
        />
      )}
      {metrics.exceptionHolds !== undefined && (
        <MetricCard
          title="Exception Holds"
          description="Requiring approval"
          value={metrics.exceptionHolds}
        />
      )}
    </div>
  );
}
